import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ViewSettings } from '../../../viewer/view-settings';
import { Icon } from '../icon/icon';
import { Tooltip } from '../tooltip/tooltip';
import { RoundButton } from './round-button';
import { ToolIcon } from './toolbar/tool-icon';
import { useViewSettings } from './toolbar/tools/view-settings/use-view-settings';

/**
 * Whether the camera flies, and the tooltip for the current camera control, ending with the one
 * clicking switches to.
 */
const useCameraControl = () => {
  const { t } = useTranslation();
  // Re-renders when the camera control changes
  useViewSettings();
  const flying = ViewSettings.instance.cameraControl === 'fly';

  const tooltip = flying
    ? {
        title: t('viewSettings.cameraControl.fly.title', { ns: 'skyviewToolbar' }),
        text: (
          <>
            {t('viewSettings.cameraControl.fly.text', { ns: 'skyviewToolbar' })}
            <SwitchTo>
              {t('viewSettings.cameraControl.orbit.switchTo', { ns: 'skyviewToolbar' })}
            </SwitchTo>
          </>
        ),
      }
    : {
        title: t('viewSettings.cameraControl.orbit.title', { ns: 'skyviewToolbar' }),
        text: (
          <>
            {t('viewSettings.cameraControl.orbit.text', { ns: 'skyviewToolbar' })}
            <SwitchTo>
              {t('viewSettings.cameraControl.fly.switchTo', { ns: 'skyviewToolbar' })}
            </SwitchTo>
          </>
        ),
      };

  return {
    flying,
    tooltip,
    toggle: () => ViewSettings.instance.setCameraControl(flying ? 'orbit' : 'fly'),
  };
};

/**
 * Switches the perspective camera between orbiting and flying freely, showing the current one.
 */
const CameraControlToggle = () => {
  const { t } = useTranslation();
  const { flying, tooltip, toggle } = useCameraControl();

  return (
    <Tooltip content={tooltip.text} mainAxisOffset={10} placement="bottom" title={tooltip.title}>
      <RoundButton
        $active={flying}
        aria-label={t('viewSettings.cameraControl.fly.title', { ns: 'skyviewToolbar' })}
        aria-pressed={flying}
        type="button"
        onClick={toggle}
      >
        <Icon icon={flying ? ['fal', 'plane'] : ['fal', 'arrows-spin']} />
      </RoundButton>
    </Tooltip>
  );
};

/**
 * The same, as a tool icon, for the timeline bar's notch. Disabled with the orthographic camera,
 * which only orbits, so the notch keeps its width as the camera changes.
 */
const CameraControlTool = ({ disabled }: { disabled: boolean }) => {
  const { flying, tooltip, toggle } = useCameraControl();

  return (
    <ToolIcon
      active={flying && !disabled}
      callback={toggle}
      disabled={disabled}
      icon={flying ? ['fal', 'plane'] : ['fal', 'arrows-spin']}
      tooltip={tooltip}
    />
  );
};

// On its own line below the description
const SwitchTo = styled.div`
  margin-top: 0.5em;
  font-style: italic;
`;

export { CameraControlToggle, CameraControlTool };
